import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 获取认证头
 */
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * 检查用户是否已登录
 */
const isUserLoggedIn = (): boolean => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

/**
 * 聊天服务API基础地址
 */
const CONVERSATION_API_BASE = process.env.REACT_APP_CONVERSATION_API_BASE || 'http://localhost:9002';

/**
 * 聊天历史本地存储Hook
 * 根据用户登录状态选择存储策略：
 * - 登录用户：优先使用数据库CRUD + localStorage缓存
 * - 匿名用户：只使用localStorage操作
 */

export interface ChatHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  type?: 'clarification' | 'confirmation' | 'analysis' | 'strategy_selection' | 'strategy_modification_prompt' | 'normal';
  caseInfo?: Record<string, any>;
  analysisCapability?: string;
  isStreaming?: boolean;
  isStreamingComplete?: boolean;
  processingTime?: number;
  files?: Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    url: string;
  }>;
  // 策略选择相关字段
  strategies?: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  currentStrategy?: string;
  canModify?: boolean;
  // 文档支持相关字段
  showDocumentSupport?: boolean;
}

export interface ChatHistoryData {
  id: string;
  sessionId?: string | null; // 后端会话ID
  messages: ChatHistoryMessage[];
  timestamp: number;
  title: string;
  conversationState?: {
    conversationId?: string | null;
    sessionId?: string | null;
    state?: string;
    stage?: string;
    caseInfo?: Record<string, any>;
    completenessScore?: number;
  };
  // 添加三阶段分析结果
  phaseResults?: Record<number, any>;
}

export interface ChatHistoryStorageState {
  currentChatId: string | null;
  chatList: ChatHistoryData[];
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: string | null;
}

const CHAT_LIST_KEY = 'chatList';
const CHAT_PREFIX = 'chat_';
const MAX_CHATS = 20; // 只保留最近20个聊天

export const useChatHistoryStorage = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<ChatHistoryStorageState>({
    currentChatId: null,
    chatList: [],
    isLoading: false,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
  });

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 生成聊天ID
   */
  const generateChatId = useCallback((): string => {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }, []);

  /**
   * 生成聊天标题
   */
  const generateChatTitle = useCallback((messages: ChatHistoryMessage[]): string => {
    if (messages.length === 0) return t('chat.newConversation');
    
    const firstUserMessage = messages.find(msg => msg.role === 'user');
    if (firstUserMessage) {
      let title = firstUserMessage.content.substring(0, 30);
      if (firstUserMessage.content.length > 30) {
        title += '...';
      }
      return title;
    }
    
    return t('chat.newConversation');
  }, [t]);

  /**
   * 从localStorage加载聊天列表
   */
  const loadChatHistoryFromLocal = useCallback((): ChatHistoryData[] => {
    try {
      const chatListStr = localStorage.getItem(CHAT_LIST_KEY);
      if (!chatListStr) return [];
      
      const chatList: any[] = JSON.parse(chatListStr);
      return chatList.map(chat => ({
        id: chat.id,
        sessionId: chat.sessionId || null,
        messages: chat.messages || [],
        timestamp: chat.timestamp || Date.now(),
        title: chat.title || '未命名对话',
        conversationState: chat.conversationState || {},
      }));
    } catch (error) {
      return [];
    }
  }, []);

  /**
   * 保存聊天数据到localStorage
   */
  const saveChatToLocal = useCallback((chatData: ChatHistoryData) => {
    try {
      // 保存单个聊天数据
      localStorage.setItem(`${CHAT_PREFIX}${chatData.id}`, JSON.stringify(chatData));
      
      // 更新聊天列表索引
      let chatList = loadChatHistoryFromLocal();
      const existingIndex = chatList.findIndex(chat => chat.id === chatData.id);
      
      if (existingIndex >= 0) {
        chatList[existingIndex] = chatData;
      } else {
        chatList.unshift(chatData);
      }
      
      // 只保留最近的聊天
      chatList = chatList.slice(0, MAX_CHATS);
      localStorage.setItem(CHAT_LIST_KEY, JSON.stringify(chatList));
      
      // 更新状态
      setState(prev => ({
        ...prev,
        chatList,
        error: null,
      }));
      
    } catch (error) {
      setState(prev => ({ ...prev, error: '保存到本地失败' }));
    }
  }, [loadChatHistoryFromLocal]);

  /**
   * 从localStorage加载特定聊天
   */
  const loadChatFromLocal = useCallback((chatId: string): ChatHistoryData | null => {
    try {
      const chatDataStr = localStorage.getItem(`${CHAT_PREFIX}${chatId}`);
      if (!chatDataStr) return null;
      
      const chatData = JSON.parse(chatDataStr);
      return {
        id: chatData.id,
        sessionId: chatData.sessionId || null,
        messages: chatData.messages || [],
        timestamp: chatData.timestamp || Date.now(),
        title: chatData.title || '未命名对话',
        conversationState: chatData.conversationState || {},
      };
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * 删除服务器聊天数据
   */
  const deleteChatFromServer = useCallback(async (chatId: string) => {
    try {
      const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/frontend/${chatId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        return true;
      } else {
        const errorData = await response.json().catch(() => ({ detail: '未知错误' }));
        throw new Error(`服务器删除失败: ${response.status} - ${errorData.detail || '未知错误'}`);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  /**
   * 删除聊天数据（根据登录状态选择策略）
   */
  const deleteChatFromLocal = useCallback(async (chatId: string) => {
    try {
      setState(prev => ({ ...prev, error: null }));
      
      // 先删除本地数据
      localStorage.removeItem(`${CHAT_PREFIX}${chatId}`);
      
      // 更新聊天列表
      let chatList = loadChatHistoryFromLocal();
      chatList = chatList.filter(chat => chat.id !== chatId);
      localStorage.setItem(CHAT_LIST_KEY, JSON.stringify(chatList));
      
      // 更新状态
      setState(prev => ({
        ...prev,
        chatList,
        currentChatId: prev.currentChatId === chatId ? null : prev.currentChatId,
      }));
      
      // 根据登录状态决定是否删除服务器数据
      if (isUserLoggedIn()) {
        // 登录用户：删除数据库中的数据
        try {
          await deleteChatFromServer(chatId);
        } catch (serverError) {
          setState(prev => ({ 
            ...prev, 
            error: `本地删除成功，但服务器删除失败: ${serverError instanceof Error ? serverError.message : '未知错误'}` 
          }));
        }
      }
      
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : '删除失败' 
      }));
    }
  }, [loadChatHistoryFromLocal, deleteChatFromServer]);

  /**
   * 同步到服务器（防抖）- 仅对登录用户
   */
  const syncToServer = useCallback(async (chatData: ChatHistoryData, immediate: boolean = false) => {
    // 匿名用户跳过同步
    if (!isUserLoggedIn()) {
      return;
    }

    // 清除之前的定时器
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    const doSync = async () => {
      try {
        setState(prev => ({ ...prev, isSyncing: true, error: null }));
        
        // 转换为服务器格式
        const frontendChatData = {
          id: chatData.id,
          session_id: chatData.sessionId,
          messages: chatData.messages.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            type: msg.type || null,
            case_info: msg.caseInfo || null,
            analysis_capability: msg.analysisCapability || null,
            is_streaming: msg.isStreaming || null,
            is_streaming_complete: msg.isStreamingComplete || null,
            processing_time: msg.processingTime || null,
            files: msg.files || null,
            // 添加策略选择相关字段
            strategies: msg.strategies || null,
            current_strategy: msg.currentStrategy || null,
            can_modify: msg.canModify || null,
          })),
          timestamp: chatData.timestamp,
          title: chatData.title,
          conversation_state: chatData.conversationState || null,
        };

        // 调用同步API
        const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/frontend/sync`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            chat_data: frontendChatData,
            force_update: false,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          
          // 更新本地数据的sessionId
          if (result.session_id && result.session_id !== chatData.sessionId) {
            const updatedChatData = {
              ...chatData,
              sessionId: result.session_id,
            };
            saveChatToLocal(updatedChatData);
          }
          
          setState(prev => ({
            ...prev,
            isSyncing: false,
            lastSyncTime: Date.now(),
          }));
          
        } else {
          throw new Error(`同步失败: ${response.status}`);
        }
        
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          isSyncing: false, 
          error: error instanceof Error ? error.message : '同步失败' 
        }));
      }
    };

    if (immediate) {
      await doSync();
    } else {
      // 防抖：延迟2秒同步
      syncTimeoutRef.current = setTimeout(doSync, 2000);
    }
  }, [saveChatToLocal]);

  /**
   * 从服务器加载聊天列表
   */
  const loadChatHistoryFromServer = useCallback(async (
    page: number = 1, 
    pageSize: number = 20, 
    overwriteLocal: boolean = false
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await fetch(`${CONVERSATION_API_BASE}/api/conversations/frontend/list?page=${page}&page_size=${pageSize}&include_messages=true`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // 转换服务器数据为本地格式
        const serverChatList: ChatHistoryData[] = result.chat_list.map((serverChat: any) => ({
          id: serverChat.id,
          sessionId: serverChat.session_id,
          messages: serverChat.messages.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            type: msg.type,
            caseInfo: msg.case_info,
            analysisCapability: msg.analysis_capability,
            isStreaming: msg.is_streaming,
            isStreamingComplete: msg.is_streaming_complete,
            processingTime: msg.processing_time,
            files: msg.files,
            // 添加策略选择相关字段
            strategies: msg.strategies || null,
            currentStrategy: msg.current_strategy || null,
            canModify: msg.can_modify || null,
          })),
          timestamp: serverChat.timestamp,
          title: serverChat.title,
          conversationState: serverChat.conversation_state,
        }));
        
        // 只在明确要求覆盖时才更新本地存储
        if (overwriteLocal) {
          serverChatList.forEach(chatData => {
            saveChatToLocal(chatData);
          });
        } else {
          // 只更新状态，不覆盖本地存储
          setState(prev => ({
            ...prev,
            // 不更新chatList，保持本地数据
          }));
        }
        
        setState(prev => ({
          ...prev,
          isLoading: false,
          lastSyncTime: Date.now(),
        }));
        
        return { serverChatList, total: result.total };
        
      } else {
        throw new Error(`加载失败: ${response.status}`);
      }
      
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : '从服务器加载失败' 
      }));
      throw error;
    }
  }, [saveChatToLocal]);

  /**
   * 创建新聊天
   */
  const createNewChat = useCallback((): ChatHistoryData => {
    const newChatId = generateChatId();
    const newChat: ChatHistoryData = {
      id: newChatId,
      sessionId: null,
      messages: [],
      timestamp: Date.now(),
      title: t('chat.newConversation'),
      conversationState: {
        conversationId: null,
        sessionId: null,
        state: 'initial',
        stage: 'free',
        caseInfo: {},
        completenessScore: 0,
      },
    };

    saveChatToLocal(newChat);

    setState(prev => ({
      ...prev,
      currentChatId: newChatId,
    }));

    return newChat;
  }, [generateChatId, saveChatToLocal, t]);

  /**
   * 更新聊天数据
   */
  const updateChatData = useCallback((
    chatId: string,
    updates: Partial<ChatHistoryData>,
    syncToServerImmediately: boolean = false
  ) => {
    const existingChat = loadChatFromLocal(chatId);
    if (!existingChat) {
      return;
    }

    const updatedChat: ChatHistoryData = {
      ...existingChat,
      ...updates,
      timestamp: Date.now(),
    };

    // 如果消息更新了，自动生成新标题
    if (updates.messages && updates.messages.length > 0) {
      updatedChat.title = generateChatTitle(updates.messages);
    }

    saveChatToLocal(updatedChat);

    // 同步到服务器
    syncToServer(updatedChat, syncToServerImmediately);
  }, [loadChatFromLocal, saveChatToLocal, generateChatTitle, syncToServer]);

  /**
   * 添加消息到聊天
   */
  const addMessageToChat = useCallback((
    chatId: string, 
    message: ChatHistoryMessage,
    syncImmediately: boolean = false
  ) => {
    const existingChat = loadChatFromLocal(chatId);
    if (!existingChat) {
      return;
    }

    const updatedMessages = [...existingChat.messages, message];
    updateChatData(
      chatId, 
      { messages: updatedMessages }, 
      syncImmediately
    );
  }, [loadChatFromLocal, updateChatData]);

  /**
   * 初始化 - 根据登录状态选择加载策略
   */
  useEffect(() => {
    const localChatList = loadChatHistoryFromLocal();
    setState(prev => ({
      ...prev,
      chatList: localChatList,
      isLoading: false, // 确保初始状态不是加载中
    }));

    // 根据登录状态决定是否从服务器加载
    if (isUserLoggedIn()) {
      // 异步加载服务器数据，不阻塞UI
      loadChatHistoryFromServer(1, 20, false)
        .then(() => {
          // 成功加载后，确保状态重置
          setState(prev => ({
            ...prev,
            isLoading: false,
          }));
        })
        .catch((error) => {
          // 失败时也要重置状态
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: '服务器数据加载失败，使用本地缓存',
          }));
        });
    }
  }, [loadChatHistoryFromLocal, loadChatHistoryFromServer]);

  /**
   * 清理定时器
   */
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  /**
   * 手动同步服务器数据（登录用户）
   */
  const manualSyncFromServer = useCallback(async () => {
    if (!isUserLoggedIn()) {
      setState(prev => ({ ...prev, error: '需要登录才能同步服务器数据' }));
      return;
    }
    
    try {
      setState(prev => ({ ...prev, error: null }));
      await loadChatHistoryFromServer(1, 20, true);
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : '手动同步失败' 
      }));
    }
  }, [loadChatHistoryFromServer]);

  /**
   * 设置当前聊天ID
   */
  const setCurrentChatId = useCallback((chatId: string | null) => {
    setState(prev => ({
      ...prev,
      currentChatId: chatId,
    }));
  }, []);

  return {
    // 状态
    currentChatId: state.currentChatId,
    chatList: state.chatList,
    isLoading: state.isLoading,
    isSyncing: state.isSyncing,
    lastSyncTime: state.lastSyncTime,
    error: state.error,
    isLoggedIn: isUserLoggedIn(),

    // 操作
    createNewChat,
    updateChatData,
    addMessageToChat,
    loadChatFromLocal,
    deleteChatFromLocal,
    deleteChatFromServer,
    loadChatHistoryFromServer,
    syncToServer,
    manualSyncFromServer,
    setCurrentChatId,

    // 工具函数
    generateChatId,
    generateChatTitle,
  };
}; 